import Image from "next/image";
import { Star } from "lucide-react";
import { Badge } from "@/components/themes/ui/badge";
import Link from "next/link";
import { addtowishlist } from "@/clicks/wishlistClicks";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { getAssetsBaseUrl, getCurrencySymbol } from "@/services/storeService";

type ProductCardProps = {
  product: {
    id: number;
    name: string;
    eng_name: string;
    mrp: number;
    sp: number;
    seo_url: string;
    imageurl: string;
    videourl?: string;
    product_tags: string | null;
    quantity: number; // Add this field
  };
  wishlistData: string[];
  setWishlistData: (data: string[]) => void;
  overallRatingData: { product_id: number; average_rating: string }[] | undefined;
  onWishlistToggle: (product: any) => void;
};

 const assets = `${getAssetsBaseUrl()}`
const fallbackImage = "/ecomsaas-assets/images/no-image.webp";

export default function ProductCard({
  product,
  wishlistData,
  setWishlistData,
  overallRatingData,
  onWishlistToggle,
}: ProductCardProps) {
  const router = useRouter();
  const discount = Math.round(((product.mrp - product.sp) / product.mrp) * 100);
  const hasGlowInDark = product.product_tags?.includes("GLOW IN DARK");
  const isOversizedFit = product.product_tags?.includes("OVERSIZED FIT");
  const isOutOfStock = product.quantity === 0; // Add this line


  // Parse image and video URLs
  const mediaUrls = product?.imageurl
    ? product.imageurl.split(",").map((url) => url.trim()).filter((url) => url)
    : [];
  // Extract video URL from imageurl if videourl is not provided
  const videoExtensions = [".mp4", ".webm", ".ogg"];
  const videoUrlFromImage = mediaUrls.find((url) =>
    videoExtensions.some((ext) => url.toLowerCase().endsWith(ext))
  );
  const imageUrls = mediaUrls.filter(
    (url) => !videoExtensions.some((ext) => url.toLowerCase().endsWith(ext))
  );

  const initialVideoUrl = product.videourl || videoUrlFromImage;
  const initialFirstImage = imageUrls[0] ? `${assets}${imageUrls[0]}` : fallbackImage;
  const initialSecondImage = imageUrls[1] ? `${assets}${imageUrls[1]}` : null;

  const [firstImageSrc, setFirstImageSrc] = useState(initialFirstImage);
  const [secondImageSrc, setSecondImageSrc] = useState(initialSecondImage);
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Debug URLs
  useEffect(() => {
    if (initialVideoUrl) {
      const videoUrl = `${assets}${initialVideoUrl}`;
      fetch(videoUrl, { method: "HEAD" })
        .then((response) => {
          if (!response.ok) {
            // console.error(`Video URL inaccessible: ${videoUrl}, Status: ${response.status}`);
            setVideoError(true);
          }
        })
        .catch((error) => {
          // console.error(`Error checking video URL: ${videoUrl}`, error);
          setVideoError(true);
        });
    } else {
      // console.warn("No video URL provided for product:", product.id);
    }
  }, [initialVideoUrl, imageUrls, product.id]);

  const getProductRating = (productId: any) => {
    const productRating = overallRatingData?.find(
      (prod) => prod.product_id === productId
    )?.average_rating;
    return productRating ? parseFloat(productRating).toFixed(1) : "0.0";
  };

  const handleWishlistClick = () => {
    setIsHeartAnimating(true);
    addtowishlist(product, wishlistData, setWishlistData, router.push);
    onWishlistToggle(product);
    setTimeout(() => setIsHeartAnimating(false), 500);
  };

  const handleImageError = (imageType: "first" | "second") => {
    console.error(`Image error for ${imageType} image:`, imageType === "first" ? firstImageSrc : secondImageSrc);
    if (imageType === "first") {
      setFirstImageSrc(fallbackImage);
    } else if (imageType === "second") {
      setSecondImageSrc(fallbackImage);
    }
  };

  const handleVideoError = () => {
    console.error("Video failed to load:", `${assets}${initialVideoUrl}`);
    setVideoError(true);
    setFirstImageSrc(fallbackImage);
  };

  return (
    <div className="relative group border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <Link href={`/p/${encodeURIComponent(product.seo_url)}`} aria-label={product.name}>
        <div className="relative w-full aspect-[4.5/5] bg-gray-100">
          {initialVideoUrl && !videoError ? (
            <video
              src={`${assets}${initialVideoUrl}`}
              autoPlay
              loop
              muted
              playsInline
              controls={false}
              className="object-cover w-full h-full transition-opacity duration-300 ease-in-out"
              onError={handleVideoError}
            />
          ) : (
            <>
              <img
                src={firstImageSrc}
                alt={product.name}
                style={{ display: "none" }}
                onError={() => handleImageError("first")}
                loading="lazy"
              />
              <Image
                src={firstImageSrc}
                alt={product.name}
                width={400}
                height={500}
                className={`object-cover object-top w-full h-full transition-opacity duration-300 ease-in-out ${secondImageSrc ? "group-hover:opacity-0" : "opacity-100"
                  }`}
                unoptimized
                loading="lazy"

              />
              {secondImageSrc && (
                <>
                  <img
                    src={secondImageSrc}
                    alt={`${product.name} - secondary`}
                    style={{ display: "none" }}
                    onError={() => handleImageError("second")}
                    loading="lazy"

                  />
                  <Image
                    src={secondImageSrc}
                    alt={`${product.name} - secondary`}
                    width={400}
                    height={500}
                    className="absolute top-0 left-0 object-cover w-full h-full transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-100"
                    unoptimized
                    loading="lazy"

                  />
                </>
              )}
            </>
          )}

          {isOutOfStock && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10">
              <div className=" text-white font-bold px-6 py-2 rounded border-2 border-error_hover text-sm tracking-wide">
                OUT OF STOCK
              </div>
            </div>
          )}

          {hasGlowInDark && (
            <Badge className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded-sm">
              GLOW IN DARK
            </Badge>
          )}
          {isOversizedFit && (
            <Badge className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded-sm">
              OVERSIZED FIT
            </Badge>
          )}
          {Number(getProductRating(product?.id)) > 0 && (
            <div className="absolute bottom-2 left-2 flex items-center bg-white/90 px-2 py-1 rounded-full text-xs font-medium">
              <div className="flex items-center">
                <Star className="w-3 h-3 fill-yellow-500 text-yellow-500 mr-1" />
                {getProductRating(product?.id)}
              </div>
            </div>
          )}
        </div>
      </Link>

      <div className="p-3">
        <div className="flex justify-between items-start">
          <Link className="w-[80%]" href={`/p/${encodeURIComponent(product.seo_url)}`}>
            <div className="truncate">
              <h2 className="text-sm font-medium text-category_name truncate">{product.name}</h2>
              {/* <h2 className="text-sm font-medium text-gray_text truncate">{product.eng_name}</h2> */}
            </div>
          </Link>
          <svg
            onClick={handleWishlistClick}
            className={`cursor-pointer w-[24px] ${isHeartAnimating ? "animate-heart" : ""}`}
            viewBox="0 0 27 27"
            fill={wishlistData.includes(product.id.toString()) ? "#f45440" : "none"}
            stroke={wishlistData.includes(product.id.toString()) ? "#f45440" : "#000"}
            strokeWidth="1.5"
          >
            <path d="M13.4993 26.2061L4.70067 16.9253C3.9281 16.1443 3.41815 15.1374 3.24307 14.0471C3.06798 12.9568 3.23664 11.8385 3.72514 10.8505V10.8505C4.09415 10.1046 4.63318 9.45803 5.29779 8.96406C5.96241 8.47008 6.73359 8.14284 7.54782 8.00931C8.36204 7.87578 9.19599 7.93978 9.98095 8.19603C10.7659 8.45228 11.4794 8.89345 12.0627 9.48319L13.4993 10.9358L14.9359 9.48319C15.5192 8.89345 16.2327 8.45228 17.0177 8.19603C17.8026 7.93978 18.6366 7.87578 19.4508 8.00931C20.265 8.14284 21.0362 8.47008 21.7008 8.96406C22.3654 9.45803 22.9045 10.1046 23.2735 10.8505V10.8505C23.762 11.8385 23.9306 12.9568 23.7556 14.0471C23.5805 15.1374 23.0705 16.1443 22.298 16.9253L13.4993 26.2061Z" />
          </svg>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-[18px] font-semibold text-category_name">{getCurrencySymbol()} {product.sp}</span>
          {product.sp !== product.mrp && (
            <span className="text-[15px] text-gray_text line-through">{getCurrencySymbol()} {product.mrp}</span>
          )}
          {/* {discount > 0 && (
            <span className="text-xs text-green-600 font-medium">{discount}% OFF</span>
          )} */}
        </div>
      </div>

      <style jsx>{`
        @keyframes heartAnimation {
          0% {
            transform: scale(1);
          }
          20% {
            transform: scale(1.3);
          }
          40% {
            transform: scale(1.1) rotate(-5deg);
          }
          60% {
            transform: scale(1.2) rotate(5deg);
          }
          80% {
            transform: scale(1.1) rotate(-3deg);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-heart {
          animation: heartAnimation 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}